import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API } from 'aws-amplify';
import { Observable } from 'rxjs';
import { ConfigService } from '../../config';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsHttpService {

  apiName: string;

  constructor(private http: HttpClient, private configService: ConfigService) {
    this.apiName = this.configService.get('host');
  }

  /**
   * Normalize API response by extracting data from nested structure if needed
   * Handles responses that may be wrapped in a 'data' property
   */
  private normalizeApiResponse(response: unknown): unknown {
    if (!response || typeof response !== 'object' || Array.isArray(response)) {
      return response;
    }

    const responseObj = response as Record<string, unknown>;

    // If response has labels and data at top level, use it directly
    if (responseObj.labels && responseObj.data) {
      return responseObj;
    }

    // If response has a data property that contains our object
    if (responseObj.data && typeof responseObj.data === 'object' && !Array.isArray(responseObj.data)) {
      const dataObj = responseObj.data as Record<string, unknown>;
      if (dataObj.labels && dataObj.data) {
        return responseObj.data;
      }
    }

    // Otherwise use response as-is
    return response;
  }

  /**
   * Get drink volume data broken down by the specified dimension
   * @param queryParams Filter parameters including breakdownBy, distributors, clients, etc.
   */
  getDrinkVolume(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/drink-volume';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        // AWS Amplify API.get returns the response body directly when response: false
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching drink volume:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get drink volume breakdown by drink (for doughnut chart)
   * Returns percentage breakdown by drink type
   * @param queryParams Filter parameters (distributors, clients, campuses, machines, date range, etc.)
   */
  getDrinkVolumeBreakdown(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/drink-volume-breakdown';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching drink volume breakdown:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get drink volume aggregated by machine
   * @param queryParams Filter parameters (distributors, clients, campuses, machines, date range, etc.)
   */
  getDrinkVolumeByMachine(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/drink-volume-by-machine';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching drink volume by machine:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get drink volume breakdown by machine (for doughnut chart)
   * Returns percentage breakdown by machine
   * @param queryParams Filter parameters (distributors, clients, campuses, machines, date range, etc.)
   */
  getDrinkVolumeMachineBreakdown(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/drink-volume-machine-breakdown';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching machine breakdown:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get product consumption data (in lbs) broken down by the specified dimension
   * @param queryParams Filter parameters including breakdownBy, distributors, clients, etc.
   */
  getConsumption(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/consumption';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching consumption:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get consumption data over time (for line chart)
   * @param queryParams Filter parameters including avgBy, distributors, clients, etc.
   */
  getConsumptionOverTime(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/consumption-over-time';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching consumption over time:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get projected consumption data (in lbs) for the forecast period
   * @param queryParams Filter parameters including forecastDays, breakdownBy, distributors, clients, etc.
   */
  getForecastConsumption(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/forecast-consumption';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching forecast consumption:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get projected consumption over time (time series for forecast)
   * @param queryParams Filter parameters including forecastDays, distributors, clients, etc.
   */
  getForecastConsumptionOverTime(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/forecast-consumption-over-time';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching forecast consumption over time:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get inventory projection over time for the forecast period
   * @param queryParams Filter parameters including forecastDays, distributors, clients, etc.
   */
  getInventoryProjection(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/inventory-projection';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching inventory projection:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get stock-out risk data for all machines and products
   * @param queryParams Filter parameters including distributors, clients, machines, products, etc.
   */
  getStockoutRisk(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/stockout-risk';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching stockout risk:', err);
        observer.error(err);
      });
    });
  }

  /**
   * Get machine stock-out risk heatmap data
   * @param queryParams Filter parameters including distributors, clients, machines, products, etc.
   */
  getStockoutHeatmap(queryParams: any): Observable<any> {
    return new Observable((observer: any) => {
      const path = '/dashboard/analytics/stockout-heatmap';
      const myInit = {
        response: false,
        queryStringParameters: queryParams
      };
      API.get(this.apiName, path, myInit).then(response => {
        const data = this.normalizeApiResponse(response);
        observer.next(data);
      }).catch(err => {
        console.error('Error fetching stockout heatmap:', err);
        observer.error(err);
      });
    });
  }
}

